import { useEffect, useRef, useState } from "react";
import { aideApi, terminalApi } from "@/lib/api-client";
import { SovereignRFQCard } from "./SovereignRFQCard";
import { LifeBuoy, X, Send, Loader2, Umbrella, Mic, Square } from "lucide-react";

const SESSION_KEY = "uc_aide_session";
const getSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

export const UmbrellaAide = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Intelligence Officer on line. State the query — responses return as an EXECUTIVE VERDICT backed by LCI FACTS drawn from your live estate data." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const scrollRef = useRef(null);

  // VOCAL PARITY — record via MediaRecorder, transcribe through managed Whisper STT
  const toggleMic = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setTranscribing(true);
        try {
          const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
          const r = await terminalApi.transcribe(blob);
          setInput((m) => (m ? m + " " : "") + r.text);
        } catch {
          setMessages((m) => [...m, { role: "assistant", content: "Transcription failed — dictate again or type the mandate." }]);
        } finally { setTranscribing(false); }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Microphone access denied — grant permission to dictate." }]);
    }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const r = await aideApi.chat(getSessionId(), text);
      setMessages((m) => [...m, { role: "assistant", content: r.reply,
                                  actions: r.meta?.actions || [], rfq: r.meta?.rfq || null }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Transmission failed — try again in a moment." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-6 z-[95] w-[360px] max-w-[calc(100vw-3rem)] border border-amber-500/30 bg-[#0C0C0C] shadow-2xl flex flex-col" style={{ height: "480px" }} data-testid="aide-panel">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-amber-500/5">
            <div className="flex items-center gap-2">
              <Umbrella size={14} className="text-amber-400"/>
              <div>
                <div className="font-mono-uc text-[9px] uppercase tracking-[0.25em] text-amber-400/80">THE SOVEREIGN UMBRELLA COMMAND</div>
                <div className="font-serif-display text-sm leading-tight">Intelligence Officer</div>
              </div>
            </div>
            <button data-testid="aide-close-btn" onClick={() => setOpen(false)} className="text-white/40 hover:text-white transition-colors"><X size={15}/></button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto uc-scroll p-3 space-y-3" data-testid="aide-messages">
            {messages.map((m, i) => (
              <div key={i} className={`text-xs leading-relaxed max-w-[85%] p-2.5 ${m.role === "user"
                ? "ml-auto bg-amber-500/10 border border-amber-500/25 text-white/90"
                : "bg-white/[0.04] border border-white/10 text-white/75"}`}>
                {m.content.replace(/\*\*([^*]+)\*\*/g, "$1").replace(/(^|\s)\*([^*\n]+)\*(?=\s|$)/g, "$1$2")}
                {(m.actions || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-white/10 flex flex-wrap gap-1.5" data-testid={`aide-actions-${i}`}>
                    {m.actions.map((a, j) => (
                      <a key={j}
                         href={a.download ? `${process.env.REACT_APP_BACKEND_URL}${a.download}` : undefined}
                         target={a.download ? "_blank" : undefined} rel="noreferrer"
                         data-testid={`aide-action-chip-${i}-${j}`}
                         title={a.detail || a.tool}
                         className={`font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border ${
                           a.ok ? "border-emerald-500/40 text-emerald-300" : "border-rose-500/40 text-rose-300"} ${
                           a.download ? "hover:bg-emerald-500/10 cursor-pointer" : "cursor-default"}`}>
                        {a.tool} {a.ok ? "· EXECUTED" : "· REFUSED"}{a.download ? " ↓" : ""}
                      </a>
                    ))}
                  </div>
                )}
                {m.rfq && <SovereignRFQCard rfq={m.rfq} />}
              </div>
            ))}
            {busy && <div className="flex items-center gap-2 text-white/40 text-xs"><Loader2 size={11} className="animate-spin"/> Directorate processing…</div>}
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2">
            <button
              data-testid="aide-mic-btn"
              onClick={toggleMic}
              disabled={transcribing}
              title={recording ? "Stop recording" : "Dictate a mandate (Whisper STT)"}
              className={`w-9 h-9 flex-none border flex items-center justify-center transition-colors disabled:opacity-40 ${
                recording
                  ? "border-rose-500/60 text-rose-400 bg-rose-500/10 animate-pulse"
                  : "border-white/15 text-white/60 hover:border-amber-500/40 hover:text-amber-400"}`}
            >
              {transcribing ? <Loader2 size={13} className="animate-spin"/> : recording ? <Square size={12}/> : <Mic size={13}/>}
            </button>
            <input
              data-testid="aide-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={recording ? "Recording — speak your mandate…" : transcribing ? "Transcribing…" : "State your mandate…"}
              className="flex-1 h-9 bg-black border border-white/10 focus:border-amber-500/40 outline-none px-2 text-xs text-white/90"
            />
            <button data-testid="aide-send-btn" onClick={send} disabled={busy || !input.trim()}
              className="w-9 h-9 border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 flex items-center justify-center disabled:opacity-40 transition-colors">
              <Send size={13}/>
            </button>
          </div>
        </div>
      )}
      <button
        data-testid="aide-bubble"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[95] w-13 h-13 p-3.5 border border-amber-500/50 bg-[#0C0C0C] text-amber-400 hover:bg-amber-500/10 shadow-xl transition-colors"
        title="Umbrella Directorate — mandate channel"
      >
        <LifeBuoy size={22}/>
      </button>
    </>
  );
};
